import { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, Spinner } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ImageUploader({ images, onImagesChange, maxImages = 5 }) {
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    const handleFileSelect = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        if (images.length + files.length > maxImages) {
            toast.error(`Maximum ${maxImages} images autorisées`);
            return;
        }

        setUploading(true);
        const uploadedUrls = [];

        for (const file of files) {
            if (!file.type.startsWith('image/')) {
                toast.error(`${file.name} n'est pas une image`);
                continue;
            }

            if (file.size > 5 * 1024 * 1024) {
                toast.error(`${file.name} dépasse 5MB`);
                continue;
            }

            try {
                const formData = new FormData();
                formData.append('file', file);
                
                const response = await axios.post(`${API}/upload`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                
                // Construire l'URL complète
                const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
                uploadedUrls.push(imageUrl);
            } catch (error) {
                console.error('Upload error:', error);
                toast.error(`Erreur upload ${file.name}`);
            }
        }

        if (uploadedUrls.length > 0) {
            onImagesChange([...images, ...uploadedUrls]);
            toast.success(`${uploadedUrls.length} image(s) uploadée(s)`);
        }

        setUploading(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const removeImage = (index) => {
        const newImages = images.filter((_, i) => i !== index);
        onImagesChange(newImages);
    };

    return (
        <div className="space-y-3">
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                data-testid="image-file-input"
            />
            
            {/* Images Grid */}
            {images.length > 0 && (
                <div className="grid grid-cols-3 gap-3">
                    {images.map((img, idx) => (
                        <div key={idx} className="relative group aspect-square rounded-lg overflow-hidden border border-border">
                            <img
                                src={img}
                                alt={`Image ${idx + 1}`}
                                className="w-full h-full object-cover"
                            />
                            <button
                                type="button"
                                onClick={() => removeImage(idx)}
                                className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                data-testid={`remove-image-${idx}`}
                            >
                                <X size={14} weight="bold" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Upload Button */}
            {images.length < maxImages && (
                <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="w-full h-24 border-dashed flex flex-col items-center justify-center gap-2"
                    data-testid="upload-image-btn"
                >
                    {uploading ? (
                        <>
                            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-current" />
                            <span className="text-sm">Upload en cours...</span>
                        </>
                    ) : (
                        <>
                            <Upload size={24} />
                            <span className="text-sm">Cliquez pour ajouter des images</span>
                            <span className="text-xs text-muted-foreground">
                                {images.length}/{maxImages} images • Max 5MB/image
                            </span>
                        </>
                    )}
                </Button>
            )}
        </div>
    );
}
